import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, MessageSquare, File, Watch} from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import EditableExpires from "./EditableExpires";
import UsStateSelect from "./UsStateSelect";

import rawStateLicenseTypes from "./stateLicenseTypes"
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";
import { customExpiresFilter, ExpiresColumnCheckboxFilter, caseInsensitiveSort, getReminderSentDisplay } from "./reactTableUtils";

const StateLicense = ({ id, providerId, state, stateLicenseType, effectiveDate, expirationDate, license, compact, notRenewed, reminderSent, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localState, setLocalState] = useState(state);
  const [localStateLicenseType, setLocalStateLicenseType] = useState(stateLicenseType);
  const [localLicense, setLocalLicense] = useState(license);
  const [localEffectiveDate, setLocalEffectiveDate] = useState(effectiveDate);
  const [localExpirationDate, setLocalExpirationDate] = useState(expirationDate);
  const [localCompact, setLocalCompact] = useState(compact);
  const [localNotRenewed, setLocalNotRenewed] = useState(notRenewed);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);

  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);
  
  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [id, providerId, documents]);

  useEffect(() => {
    if (!editing) {
      setLocalLicense(license);
      setLocalEffectiveDate(effectiveDate);
      setLocalExpirationDate(expirationDate);
      setLocalCompact(compact);
      setLocalNotRenewed(notRenewed);
      setInitialDocumentFilenames();
    }
  }, [effectiveDate, expirationDate, license, compact, notRenewed, documents]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "statelicense/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalState(state);
    setLocalStateLicenseType(stateLicenseType);
    setLocalLicense(license);
    setLocalEffectiveDate(effectiveDate);
    setLocalExpirationDate(expirationDate);
    setLocalCompact(compact);
    setLocalNotRenewed(notRenewed);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();
    if (localState === '' || localLicense === '')
    {
      alert("Please enter a state and license");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), state: localState, stateLicenseType: localStateLicenseType, license: localLicense, compact: localCompact, effectiveDate: localEffectiveDate, expirationDate: localExpirationDate, notRenewed: localNotRenewed, documentFilenames: documentFilenames},
      path: id ? "statelicense/" + id  : "statelicense",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalState(state);
        setLocalStateLicenseType(stateLicenseType);
        setLocalEffectiveDate(effectiveDate);
        setLocalExpirationDate(expirationDate);
        setLocalLicense(license);
        setLocalCompact(compact);
        setLocalNotRenewed(notRenewed);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }


  function handleStateChanged(name, newValue) {
    setLocalState(newValue);
  }
  
  function handleStateLicenseTypeChanged(e) {
    e.preventDefault();
    setLocalStateLicenseType(e.target.value);
  }

  function handleLicenseChanged(e) {
    e.preventDefault();
    setLocalLicense(e.target.value);
  }

  function handleEffectiveDateChanged(val, e) {
    e.preventDefault();
    setLocalEffectiveDate(val);
  }

  function handleExpirationDateChanged(val, e) {
    e.preventDefault();
    setLocalExpirationDate(val);
  }

  function handleCompactChanged(e) {
    setLocalCompact(e.target.checked);
  }

  function handleNotRenewedChanged(e) {
    setLocalNotRenewed(e.target.checked);
  }

  let stateLicenseTypeList = rawStateLicenseTypes.map((stateLicenseType, i) => {
    return (
      <option key={i} value={stateLicenseType.value}>{stateLicenseType.label}</option>
    )
  }, this);

  var selectedStateLicenseTypeLabel = '';
  if (localStateLicenseType !== '')
  {
      for (let i = 0; i < rawStateLicenseTypes.length; i++)
      {
          if (localStateLicenseType === rawStateLicenseTypes[i].value)
          {
            selectedStateLicenseTypeLabel = rawStateLicenseTypes[i].label;
            break;
          }
      }
  }

  return (
    <>
    {editing ?
    <td style={{minWidth: '90px'}}>
      <UsStateSelect originalValue={localState} name="licenseState" onValueChanged={handleStateChanged} small={true} />
    </td>
    :
    <td style={
      !localState || localState.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localState}
    </td>
    }
    {editing ?
    <td>
      <Form.Select size="sm" value={localStateLicenseType} onChange={(e) => {handleStateLicenseTypeChanged(e)}} style={{width: "auto"}} >
          {stateLicenseTypeList}
      </Form.Select>
    </td>
      :
    <td style={
      !selectedStateLicenseTypeLabel || selectedStateLicenseTypeLabel.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
      }>
        {selectedStateLicenseTypeLabel}
      </td>
    }
    {editing ?
    <td>
      <Form.Control size="sm" maxLength={255} style={{minWidth: '80px'}} value={localLicense} onChange={(e) => {handleLicenseChanged(e)}} />
    </td>
    :
    <td style={
      !localLicense || localLicense.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localLicense}
    </td>
    }
    {editing ?
    <td>
      <Form.Check size="sm" checked={!!localCompact} onChange={(e) => {handleCompactChanged(e)}} />
    </td>
    :
    <td>
      {localCompact ? <Check style={{'height': '16px', 'width': '16px'}}/> : <>&nbsp;</>}
    </td>
    }
    <td>
    <EditableExpires warnWithColors={false} localExpires={localEffectiveDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEffectiveDateChanged} blankText=""/>
    </td>
    <td style={
      !localExpirationDate || localExpirationDate.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
    <EditableExpires localExpires={localExpirationDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleExpirationDateChanged} blankText=""/>
    </td>
    {editing ?
    <td>
      <Form.Check size="sm" checked={localNotRenewed} onChange={(e) => {handleNotRenewedChanged(e)}} />
    </td>
      :
    <td>
        {localNotRenewed ? <Watch style={{'height': '16px', 'width': '16px'}}/> : <>&nbsp;</>}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"statelicense"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"statelicense"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    {<td>{getReminderSentDisplay(reminderSent)}</td>}
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const StateLicensesTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;
  
  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "State",
        accessor: "state",
        key: "state",
      },
      {
        Header: "Type",
        accessor: "stateLicenseType",
        key: "stateLicenseType",
      },
      {
        Header: "License",
        accessor: "license",
        key: "license",
      },
      {
        Header: "Compact",
        accessor: "compact",
        key: "compact",
      },
      {
        Header: "Issue Date",
        accessor: "effectiveDate",
        key: "effectiveDate",
      },
      {
        Header: "Expiration",
        accessor: "expirationDate",
        key: "expirationDate",
        Filter: ExpiresColumnCheckboxFilter,
        filter: customExpiresFilter("expirationDate")
      },
      {
        Header: "Not Renewed",
        accessor: "notRenewed",
        key: "notRenewed",
      },
    ];

    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }

    baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Reminder Sent</Tooltip>}
          >
            <span>
              <MessageSquare size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "reminderSent",
        key: "reminderSent",
      },
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    );

    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
            {
                id: 'state',
                desc: false
            }
        ],
        filters: [
          {
              id: 'expirationDate',
              value: 'excludeGreaterThan30Days',
          },
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  // Extract the expires filter
  var expiresFilter = "";
  for (var i=0; i < headerGroups.length; i++)
  {
    for (var j=0; j < headerGroups[i].headers.length; j++)
    {
      var column = headerGroups[i].headers[j];
      if (column.id === "expirationDate")
      {
        expiresFilter = column.render("Filter");
        break;
      }
    }
  }

  return (
    <>
      <Table className="table-sm" striped bordered hover {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} style={{whiteSpace: "nowrap"}}>
            {headerGroup.headers.map((column) => {
              const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "reminderSent" || column.id === "documents") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
              return (
                <th {...restHeaderProps} key={key} style={(column.id === "notRenewed" || column.id === "compact") ? {...restHeaderProps.style, whiteSpace: "normal"} : restHeaderProps.style}>
                  {column.render("Header")}
                  {column.id !== "icons" && column.id !== "reminderSent" && column.id !== "documents" ? (
                    <>
                    {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                        ) : (
                          <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                    )}
                    </>
                  ) : <></>
                  }
                </th>
              )
            }
            )}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {
        error ? (<tr><td colSpan={10}>Something went wrong; please reload the page...</td></tr>) : ( 
          !isLoaded ? (<tr><td colSpan={10}>Loading...</td></tr>) : (
            rows.map((row) => {
            prepareRow(row);
            const { key, ...restRowProps } = row.getRowProps();
            return (
              <tr {...restRowProps} key={key} 
                style={{
                  ...(row.values.notRenewed ? {opacity: 0.5} : {}),
                  ...(row.values.license && row.values.license.toLowerCase().indexOf("please verify") >= 0
                    ? {
                        // td's read these from the tr; setting them here colors the whole row
                        ["--bs-table-bg"]: "rgba(255,193,7,0.3)",
                        // cancel the striped overlay so it doesn't stack on the warning color
                        ["--bs-table-striped-bg"]: "transparent",
                        ["--bs-table-accent-bg"]: "transparent"
                      }
                    : {})
                }}>
                <StateLicense providerId={providerId} id={row.values.id} key={row.values.id} state={row.values.state ? row.values.state : ""} stateLicenseType={row.values.stateLicenseType ? row.values.stateLicenseType : ""} license={row.values.license ? row.values.license : ""} effectiveDate={row.values.effectiveDate} expirationDate={row.values.expirationDate} compact={row.values.compact == null ? false : row.values.compact} notRenewed={row.values.notRenewed} reminderSent={row.values.reminderSent} documents={row.values.documents} refreshList={refreshList} canEdit={canEdit}/>
              </tr>
            );
          })))
        }
        {
        addingNew ?
          <tr><StateLicense providerId={providerId} key={0} startInEditMode={true} state={""} stateLicenseType={""} license={""}  effectiveDate={null} expirationDate={null} compact={false} notRenewed={false} reminderSent={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
        : <></>
        }
      </tbody>
    </Table>
    {expiresFilter}
  </>
  )
}

const StateLicenses = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "statelicense/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, state: item.state, stateLicenseType: item.stateLicenseType, license: item.license, effectiveDate: item.effectiveDate, expirationDate: item.expirationDate, compact: item.compact, notRenewed: item.notRenewed, reminderSent: item.reminderSent, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>State Licenses
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <StateLicensesTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New State License</Button>
          </span>
          : <></>
          }
      </Card.Body>
    </Card>
  )
}

export default StateLicenses;
